import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Globe, Clock, Send } from "lucide-react";
import { useLanguage } from "@/i18n";
import SectionHead, { EASE, reveal } from "@/components/SectionHead";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

// Static native submission via FormSubmit (free service, no backend, no fetch).
// First submission sends an activation link to the recipient mailbox — click it once.
const FORM_ACTION = "https://formsubmit.co/wifi@pronet-uae.com";

export default function Contact() {
  const { t, lang } = useLanguage();

  const info = [
    { icon: Mail, label: t.contact.emailL, value: "wifi@pronet-uae.com" },
    { icon: MapPin, label: t.contact.hqL, value: t.contact.hq },
    { icon: Globe, label: t.contact.covL, value: t.contact.cov },
    { icon: Clock, label: t.contact.respL, value: t.contact.resp },
  ];

  // Client-side gate only: blocks invalid submissions from leaving the page.
  // When valid, the form submits natively (no fetch, no axios, no backend).
  const submit = (e) => {
    const data = new FormData(e.currentTarget);
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(data.get("email") || ""));
    const ok =
      ["name", "company", "message"].every((k) => String(data.get(k) || "").trim().length > 0) &&
      emailOk &&
      data.get("consent_gdpr") === "yes";
    if (!ok) {
      e.preventDefault();
      toast.error(t.contact.toastErr);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden py-28 sm:py-32" data-testid="contact-section">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />
      <div className="pointer-events-none absolute bottom-0 left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-amber-500/10 blur-[140px]" aria-hidden="true" />

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          {...reveal}
          transition={{ duration: 0.8, ease: EASE }}
          data-testid="contact-panel"
          className="glass overflow-hidden rounded-3xl"
        >
          <div className="grid lg:grid-cols-2">
            <div className="relative p-8 sm:p-12">
              <div className="grid-bg absolute inset-0 [mask-image:radial-gradient(ellipse_at_top_left,black,transparent_75%)]" aria-hidden="true" />
              <div className="relative">
                <p className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-orange-400">
                  <span className="h-px w-8 bg-orange-400" />
                  {t.contact.eyebrow}
                </p>
                <h2
                  data-testid="contact-title"
                  className="mt-5 font-display text-4xl font-bold tracking-tighter text-slate-50 sm:text-5xl"
                >
                  {t.contact.h2}
                </h2>
                <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
                  {t.contact.body}
                </p>

                <div className="mt-10 space-y-5" data-testid="contact-info">
                  {info.map((row) => (
                    <div key={row.label} className="flex items-start gap-4">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-700/50 bg-slate-950/60 text-orange-300">
                        <row.icon className="h-4 w-4" />
                      </span>
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                          {row.label}
                        </div>
                        <div className="text-sm font-medium text-slate-200">{row.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative border-t border-slate-800/60 bg-slate-950/40 p-8 sm:p-12 lg:border-l lg:border-t-0">
              <form
                action={FORM_ACTION}
                method="POST"
                onSubmit={submit}
                noValidate
                data-testid="contact-form"
              >
                {/* FormSubmit configuration (hidden fields) */}
                <input type="hidden" name="_subject" value={`New website lead — PRONET (${lang.toUpperCase()})`} />
                <input type="hidden" name="_template" value="table" />
                <input type="hidden" name="_captcha" value="false" />
                <input type="hidden" name="_blacklist" value="casino,viagra,crypto" />
                <input type="hidden" name="language" value={lang.toUpperCase()} />
                <input type="hidden" name="_next" value={`${typeof window !== "undefined" ? window.location.origin : ""}/#sent`} />

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="cf-name" className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                      {t.contact.name} *
                    </Label>
                    <Input
                      id="cf-name"
                      name="name"
                      data-testid="contact-input-name"
                      maxLength={120}
                      autoComplete="name"
                      className="h-11 rounded-xl border-slate-700/60 bg-slate-900/60 text-slate-100 placeholder:text-slate-600 focus-visible:ring-orange-400/60"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cf-company" className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                      {t.contact.company} *
                    </Label>
                    <Input
                      id="cf-company"
                      name="company"
                      data-testid="contact-input-company"
                      maxLength={120}
                      autoComplete="organization"
                      className="h-11 rounded-xl border-slate-700/60 bg-slate-900/60 text-slate-100 placeholder:text-slate-600 focus-visible:ring-orange-400/60"
                    />
                  </div>
                </div>

                <div className="mt-5 space-y-2">
                  <Label htmlFor="cf-email" className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                    {t.contact.email} *
                  </Label>
                  <Input
                    id="cf-email"
                    name="email"
                    type="email"
                    data-testid="contact-input-email"
                    maxLength={254}
                    autoComplete="email"
                    className="h-11 rounded-xl border-slate-700/60 bg-slate-900/60 text-slate-100 placeholder:text-slate-600 focus-visible:ring-orange-400/60"
                  />
                </div>

                <div className="mt-5 space-y-2">
                  <Label htmlFor="cf-message" className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                    {t.contact.msg} *
                  </Label>
                  <Textarea
                    id="cf-message"
                    name="message"
                    data-testid="contact-input-message"
                    maxLength={5000}
                    rows={5}
                    className="resize-none rounded-xl border-slate-700/60 bg-slate-900/60 text-slate-100 placeholder:text-slate-600 focus-visible:ring-orange-400/60"
                  />
                </div>

                <div className="mt-5 flex items-start gap-3 rounded-xl border border-slate-800/60 bg-slate-900/40 p-4">
                  <Checkbox
                    id="cf-consent"
                    name="consent_gdpr"
                    value="yes"
                    data-testid="contact-consent"
                    onCheckedChange={() => {}}
                    className="mt-0.5 border-slate-600 data-[state=checked]:border-orange-400 data-[state=checked]:bg-orange-400 data-[state=checked]:text-slate-950"
                  />
                  <Label
                    htmlFor="cf-consent"
                    className="text-xs leading-relaxed text-slate-400 cursor-pointer"
                    data-testid="contact-consent-label"
                  >
                    {t.contact.consent}
                  </Label>
                </div>

                <Button
                  type="submit"
                  data-testid="contact-submit"
                  className="group mt-5 h-12 w-full rounded-full bg-orange-400 text-sm font-semibold text-slate-950 shadow-[0_0_32px_rgba(251,146,60,0.35)] transition-all duration-300 hover:bg-orange-300 hover:shadow-[0_0_44px_rgba(251,146,60,0.55)]"
                >
                  {t.contact.submit}
                  <Send className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </Button>

                {/* Honeypot: humans never see or fill this; bots often do. FormSubmit discards it. */}
                <div className="absolute -left-[9999px]" aria-hidden="true">
                  <label htmlFor="cf-website">Website</label>
                  <input id="cf-website" name="_honey" type="text" tabIndex={-1} autoComplete="off" />
                </div>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
